import i18n from '../i18n';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@mui/material';
import ProgressButtons from './common/Buttons/ProgressButtons';
import { ProfileInfo } from './common/props-types';
import { UpdatePartialProfile, UpdateNewsletterPrefs } from '../services/profile/ProfileService';
import { UpdateUserInterests } from '../services/interests/InterestService';
import { SubscribeToNewsletter } from '../services/newsletter/NewsLetterService';
import { UpdateUserInterestsPayload } from '../services/interests/UpdateUserInterestsPayload';
import CommunicationPreferencesForm, {
  CommunicationPreferencesFormValues,
} from './common/Forms/CommunicationPreferencesForm';
import { noChoice } from '../common/FLSConst';
import { SetConsentValues } from 'pages/preferences/ConsentValue';
import GoogleTagManager from 'common/GoogleTagManager';

const CommunicationPreferences = (props: {
  stepHandler: (value: number | ((prevVar: number) => number)) => void;
  currentStep: number;
  profileAuth: { sessionId: string; token: string };
  profileHandler: (value: ProfileInfo | ((prevVar: ProfileInfo) => ProfileInfo)) => void;
  profile: ProfileInfo;
}): JSX.Element => {
  const { t } = useTranslation('component');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [newsletterTypesSlug, setNewsletterTypesSlug] = useState<string[]>([]);
  const [interestsSlug, setInterestsSlug] = useState<string[]>([]);

  const currentLanguage = i18n.resolvedLanguage;
  const isFamiliplus = props.profile.familiplusCard && props.profile.familiplusCard?.length >= 10;
  const disableContentType = isFamiliplus ? false : null;
  const hasMobilePhone = props.profile.mobilePhone == null || props.profile.mobilePhone.trim() === '' ? false : true;

  const handleSave = async (values: CommunicationPreferencesFormValues) => {
    if (!isLoading) {
      setIsLoading(true);
      setErrorMessage('');
      try {
        //Les arrays sortent en string a la place de nombre donc on doit les convertir
        let cleanedInterests = values.interestsChecked.map(function (item: string) {
          return parseInt(item, 10);
        });
        const cleanedNewsletterTypes = values.newsletterTypes.map(function (item: string) {
          return parseInt(item, 10);
        });

        const updatePromises = [];

        if (values.newsletterConsent || isFamiliplus) {
          if (
            (props.profile.familiplusCard == undefined || props.profile.familiplusCard?.length < 10) &&
            !props.profile.oldProfileWasNewsletter
          ) {
            await SubscribeToNewsletter(props.profile.email, values.language);
          }

          updatePromises.push(
            UpdateNewsletterPrefs(props.profileAuth.sessionId, {
              ExplicitConsent: SetConsentValues(values.newsletterConsent, props.profile.newsletterConsent ?? false),
              NewsletterTypes: cleanedNewsletterTypes,
            })
          );
        }

        if (values.interestsChecked.length == 0) {
          cleanedInterests = noChoice;
        }

        props.profile.interests = cleanedInterests;
        props.profile.smsConsent = hasMobilePhone === true ? values.smsConsent : undefined;
        props.profile.newsletterConsent = values.newsletterConsent;
        props.profile.newsletterTypes = cleanedNewsletterTypes;
        props.profile.language = values.language;
        props.profile.profilingConsent = values.profilingConsent;
        props.profileHandler((prevProfile) => {
          return { ...prevProfile, ...props.profile };
        });

        updatePromises.push(UpdatePartialProfile(props.profileAuth.sessionId, props.profile));

        const updateUserInterestsPayload: UpdateUserInterestsPayload = {
          NoChild: (props.profile.childBirthYear?.filter((element) => element > 0).length || 0) == 0,
          ChildrenYearOfBirth: props.profile.childBirthYear!,
          InterestIds: cleanedInterests,
        };
        updatePromises.push(UpdateUserInterests(props.profileAuth.sessionId, updateUserInterestsPayload));

        await Promise.all(updatePromises);

        GoogleTagManager.sendMemberCommunication(
          props.profile.language,
          props.profile.newsletterConsent,
          newsletterTypesSlug,
          props.profile.smsConsent,
          props.profile.profilingConsent,
          interestsSlug
        );

        props.stepHandler(5);
      } catch (e) {
        setErrorMessage(t('Errors.GenericError') as string);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const initialValues: CommunicationPreferencesFormValues = {
    newsletterConsent: props.profile.newsletterConsent || false,
    newsletterTypes: props.profile.newsletterTypes ? props.profile.newsletterTypes.map((id) => id.toString()) : [],
    smsConsent: props.profile.smsConsent || false,
    language: props.profile.language ?? currentLanguage ?? '',
    interestsChecked: props.profile.interests ? props.profile.interests.map((id) => id.toString()) : [],
    profilingConsent: props.profile.profilingConsent || false,
    hasMobilePhone: hasMobilePhone,
  };

  return (
    <>
      <Typography variant="h1" sx={{ mb: '16px' }}>
        {t('CommunicationPreferences.Title')}
      </Typography>

      <CommunicationPreferencesForm
        disableNewsletterContent={disableContentType}
        communicationPreferences={initialValues}
        saveHandler={handleSave}
        saveErrorMessage={errorMessage}
        footer={
          <ProgressButtons
            stepHandler={props.stepHandler}
            currentStep={props.currentStep}
            hasPrevious={true}
            hasSkip={false}
            customSubmitText={t('CommunicationPreferences.SubmitButton')}
            isLoading={isLoading}
          />
        }
        showNewsletterPtsBox={props.profile.familiplusCard != undefined && props.profile.familiplusCard.length > 0}
        showInterestPtsBox={props.profile.familiplusCard != undefined && props.profile.familiplusCard.length > 0}
        setNewsletterTypesSlug={setNewsletterTypesSlug}
        setInterestsSlug={setInterestsSlug}
        showSmsPtsBox={props.profile.familiplusCard != undefined && props.profile.familiplusCard.length > 0}
      />
    </>
  );
};
export default CommunicationPreferences;
